import { Button, ButtonGroup, LegacyStack, Modal, Select, Text, TextField } from '@shopify/polaris';
import { useContext, useEffect, useMemo, useState } from 'react';
import ToastContext from '../../../../context/ToastContext';
import { SET_ERROR, SET_OK } from '../../../../context/ToastReducer';
import UserContext from '../../../../context/UserContext';
import { NEW_FILE_FAILED, NEW_FILE_LOADED, NEW_FILE_LOADING } from '../../../../context/UserReducer';
import {
  getReportsReplenishmentAnalysisPdf,
  getReportsReplenishmentExcel,
  getReportsReplenishmentSeriesPdf,
} from '../../../../services/reports/replenishmentService';
import { triggerBlobDownload } from '../efficacy/downloads';
import { formatRangeLabel, toApiDate } from './constants';

const fmtDate = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}${m}${dd}`;
};

const buildDefaultFilename = ({ kind, range, validFilters, depositsValue }) => {
  const dateLast = new Date(range.toExclusive.getTime() - 24 * 60 * 60 * 1000);
  const base = `reposicion_${kind}_${fmtDate(range.from)}_${fmtDate(dateLast)}`;
  const parts = [];
  const shorten = (vals) =>
    (vals || []).map((v) =>
      String(v || '')
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .replace(/[^a-zA-Z0-9]/g, '')
        .toUpperCase()
        .slice(0, 4),
    ).filter(Boolean);
  if (validFilters?.genders) parts.push(...shorten(validFilters.genders.split(':')));
  if (validFilters?.categories) parts.push(...shorten(validFilters.categories.split(':')));
  if (validFilters?.subcategories) parts.push(...shorten(validFilters.subcategories.split(':')));
  if (depositsValue && depositsValue !== 'ALL') {
    parts.push(...String(depositsValue).split(':').filter(Boolean));
  } else if (depositsValue === 'ALL') {
    parts.push('ALL');
  }
  const suffix = parts.length ? `_${parts.join('_')}` : '';
  const full = `${base}${suffix}`;
  return full.length > 80 ? `${full.slice(0, 77)}...` : full;
};

const MODE_LABELS = { analisis: 'PDF Análisis', series: 'PDF por Series', excel: 'Excel' };
const MODE_FILE_KIND = { analisis: 'analisis', series: 'series', excel: 'datos' };

// Mismas claves que las columnas sortables de `SeriesTable.jsx`. El back
// las acepta tal cual a través de `parseReplenishmentFilters`.
const SERIES_SORT_FIELDS = [
  { label: 'Serie', value: 'family' },
  { label: 'Compra (uds)', value: 'buyUnits' },
  { label: 'Venta (uds)', value: 'sellUnits' },
  { label: 'Stock (uds)', value: 'remainUnits' },
  { label: 'Rotación', value: 'rotation' },
  { label: 'Cobertura', value: 'coverage' },
  { label: 'Rentabilidad', value: 'profitability' },
  { label: 'PVP', value: 'pvpUnit' },
];

/**
 * Modal de exportación del informe de Reposición. Soporta 3 modos:
 *   - 'analisis' (PDF Análisis): toggles de capítulos.
 *   - 'series' (PDF por Series): selector de orden + dirección.
 *   - 'excel': sólo título/subtítulo/filename.
 */
export const ExportModal = ({ open, mode, onClose, range, currency, exportParamsArgs }) => {
  const [, dispatchUser] = useContext(UserContext);
  const [, dispatchToast] = useContext(ToastContext);

  const defaultSubtitle = useMemo(() => formatRangeLabel(range.from, range.toExclusive), [range]);
  const defaultFilename = useMemo(
    () =>
      buildDefaultFilename({
        kind: MODE_FILE_KIND[mode] || mode || 'analisis',
        range,
        validFilters: exportParamsArgs?.validFilters,
        depositsValue: exportParamsArgs?.depositsValue,
      }),
    [range, exportParamsArgs, mode],
  );

  const [title, setTitle] = useState('Informe de Reposición');
  const [subtitle, setSubtitle] = useState(defaultSubtitle);
  const [filename, setFilename] = useState(defaultFilename);
  const [includeCover, setIncludeCover] = useState(true);
  const [includeResumen, setIncludeResumen] = useState(true);
  const [includeSeries, setIncludeSeries] = useState(true);
  const [includeTendencia, setIncludeTendencia] = useState(true);
  const [sortBy, setSortBy] = useState('family');
  const [sortDir, setSortDir] = useState('asc');
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTitle('Informe de Reposición');
    setSubtitle(defaultSubtitle);
    setFilename(defaultFilename);
    setIncludeCover(true);
    setIncludeResumen(true);
    setIncludeSeries(true);
    setIncludeTendencia(true);
    setSortBy('family');
    setSortDir('asc');
  }, [open, mode, defaultSubtitle, defaultFilename]);

  if (!open) return null;

  const handleExport = async () => {
    setDownloading(true);
    const fileId = `${filename}-${Date.now()}`;
    dispatchUser({
      type: NEW_FILE_LOADING,
      payload: { id: fileId, type: MODE_LABELS[mode] || mode, name: title || filename, date: new Date() },
    });
    dispatchToast({ type: SET_OK, payload: { message: 'Generando export, puede tardar' } });
    onClose();

    try {
      const base = {
        // Mismo serializador que la pantalla (`useReplenishmentData`): ISO con
        // offset de México. Con `toISOString()` el export salía en UTC del
        // navegador y cubría un período distinto del que se está viendo.
        dateFrom: toApiDate(range.from),
        dateToExclusive: toApiDate(range.toExclusive),
        ...(exportParamsArgs?.validFilters || {}),
        currency: currency?.symbol,
        exchange: currency?.exchange,
        title,
        subtitle,
        filename,
      };
      if (exportParamsArgs?.depositsValue) base.deposits = exportParamsArgs.depositsValue;
      if (exportParamsArgs?.depositRolesValue) base.depositRoles = exportParamsArgs.depositRolesValue;
      if (exportParamsArgs?.depositGroupsValue) base.depositGroups = exportParamsArgs.depositGroupsValue;
      if (exportParamsArgs?.familyQuery) base.family = exportParamsArgs.familyQuery;

      let resp;
      let ext;
      if (mode === 'excel') {
        resp = await getReportsReplenishmentExcel(base);
        ext = 'xlsx';
      } else if (mode === 'series') {
        resp = await getReportsReplenishmentSeriesPdf({ ...base, sortBy, sortDir });
        ext = 'pdf';
      } else {
        resp = await getReportsReplenishmentAnalysisPdf({
          ...base,
          includeCover: includeCover ? 'true' : 'false',
          includeResumen: includeResumen ? 'true' : 'false',
          includeSeries: includeSeries ? 'true' : 'false',
          includeTendencia: includeTendencia ? 'true' : 'false',
        });
        ext = 'pdf';
      }
      const finalName = filename.endsWith(`.${ext}`) ? filename : `${filename}.${ext}`;
      const blobUrl = URL.createObjectURL(resp.data);
      triggerBlobDownload(resp.data, finalName);
      window.open(blobUrl, '_blank', 'noopener');

      dispatchUser({ type: NEW_FILE_LOADED, payload: { id: fileId, url: blobUrl } });
      dispatchToast({ type: SET_OK, payload: { message: 'Export generado' } });
    } catch (e) {
      dispatchUser({ type: NEW_FILE_FAILED, payload: { id: fileId } });
      dispatchToast({
        type: SET_ERROR,
        payload: { message: e?.response?.data?.message || 'Error generando el export' },
      });
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Exportar — ${MODE_LABELS[mode] || ''}`}
      primaryAction={{ content: 'Exportar', onAction: handleExport, loading: downloading }}
      secondaryActions={[{ content: 'Cancelar', onAction: onClose, disabled: downloading }]}
    >
      <Modal.Section>
        <LegacyStack vertical>
          <TextField label="Nombre del fichero" value={filename} onChange={setFilename} autoComplete="off" />
          <TextField label="Título" value={title} onChange={setTitle} autoComplete="off" />
          <TextField label="Subtítulo" value={subtitle} onChange={setSubtitle} autoComplete="off" />
        </LegacyStack>
      </Modal.Section>
      {mode === 'analisis' && (
        <Modal.Section>
          <LegacyStack vertical>
            <Text variant="bodyMd" fontWeight="semibold">
              Capítulos a incluir
            </Text>
            <ButtonGroup spacing="tight">
              <Button primary={includeCover} onClick={() => setIncludeCover((v) => !v)}>
                Portada
              </Button>
              <Button primary={includeResumen} onClick={() => setIncludeResumen((v) => !v)}>
                Resumen
              </Button>
              <Button primary={includeSeries} onClick={() => setIncludeSeries((v) => !v)}>
                Series destacadas
              </Button>
              <Button primary={includeTendencia} onClick={() => setIncludeTendencia((v) => !v)}>
                Evolución mensual
              </Button>
            </ButtonGroup>
          </LegacyStack>
        </Modal.Section>
      )}
      {mode === 'series' && (
        <Modal.Section>
          <LegacyStack vertical>
            <Text variant="bodyMd" fontWeight="semibold">
              Orden
            </Text>
            <Select label="Ordenar por" options={SERIES_SORT_FIELDS} value={sortBy} onChange={setSortBy} />
            <div>
              <Text variant="bodyMd" tone="subdued" as="span">
                Dirección
              </Text>
              <div style={{ marginTop: 6 }}>
                <ButtonGroup segmented>
                  <Button primary={sortDir === 'asc'} onClick={() => setSortDir('asc')}>
                    Ascendente
                  </Button>
                  <Button primary={sortDir === 'desc'} onClick={() => setSortDir('desc')}>
                    Descendente
                  </Button>
                </ButtonGroup>
              </div>
            </div>
          </LegacyStack>
        </Modal.Section>
      )}
    </Modal>
  );
};
